/**
 * deviceDetect.ts — Pure-function device + environment detection
 *
 * BETA 16.70 — moved out of TouchControls.tsx so non-React code (engine,
 * livePlayTracker) can use the same detection without dragging React
 * imports into core game logic.
 *
 * All checks are SSR-safe (guarded by `typeof window !== 'undefined'`).
 */

export function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  const hasTouchEvents = 'ontouchstart' in window || (navigator.maxTouchPoints ?? 0) > 0;
  let coarsePointer = false;
  try {
    coarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  } catch { /* ignore */ }
  return hasTouchEvents || coarsePointer;
}

export function isIPhoneSafari(): boolean {
  if (typeof window === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIPhone = /iPhone|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIPhone && isSafari;
}

export function isStandalonePWA(): boolean {
  if (typeof window === 'undefined') return false;
  const navAny = navigator as unknown as { standalone?: boolean };
  if (navAny.standalone === true) return true;
  try {
    return window.matchMedia?.('(display-mode: standalone)').matches ?? false;
  } catch { return false; }
}

/** Coarse classification of mobile vs tablet vs desktop, mostly for
 *  Supabase telemetry tagging. UA sniffing is imperfect but adequate
 *  for sorting bugs by form factor.
 *
 *  BETA 16.71 — Per Dylan: "i wat to be able to play this game on my
 *  xbox rog ally x." Added handheld + console classes so PSBOT can
 *  filter bug reports specifically for handheld / console form factors.
 *  ROG Ally / Steam Deck / Xbox / PS5 web browsers all have specific
 *  UA hints we can sniff. */
export type DeviceClass =
  | 'iphone'
  | 'ipad'
  | 'android-phone'
  | 'android-tablet'
  | 'steam-deck'
  | 'rog-ally'
  | 'xbox-console'
  | 'playstation-console'
  | 'nintendo-switch'
  | 'desktop';

export function classifyDevice(): DeviceClass {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent.toLowerCase();

  // Handheld + console UA hints first — they're more specific than mobile.
  // Steam Deck reports as Linux + a SteamDeck token in some configs.
  if (/steamdeck|steam.deck/.test(ua)) return 'steam-deck';
  // ROG Ally / ROG Xbox Ally — direct UA hits.
  if (/rog ally|rog.xbox.ally|asus_ai2401|asus.ally/.test(ua)) return 'rog-ally';
  // Xbox Series X|S / Xbox One web browser (Edge / Internet Explorer).
  if (/xbox/.test(ua)) return 'xbox-console';
  // PlayStation 5 / PS4 native browser.
  if (/playstation 5|playstation 4|ps5; |ps4; /.test(ua)) return 'playstation-console';
  // Nintendo Switch web browser.
  if (/nintendo.switch/.test(ua)) return 'nintendo-switch';

  if (/iphone|ipod/.test(ua)) return 'iphone';
  // iPad on iPadOS 13+ reports as Mac — distinguish via maxTouchPoints
  if (/ipad/.test(ua) || (/macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1)) return 'ipad';
  if (/android/.test(ua)) {
    // Android tablets often have "tablet" or no "mobile" in UA
    if (!/mobile/.test(ua)) return 'android-tablet';
    return 'android-phone';
  }

  // BETA 16.75 — Per Dylan playing on ROG Ally X: "and on the rog ally
  // version its acting like i am on cell phon cause i see the Q and E
  // circle buttons in game on screen." The rog-ally UA regex above
  // didn't match — Edge on the Ally X likely reports as a generic
  // Windows + Edge UA without ASUS-specific tokens (it's just Windows 11
  // running Edge in a window). Fallback heuristic: any Windows machine
  // with a TOUCH SCREEN AND a small-handheld viewport (<= 1366 wide,
  // which covers Ally / Ally X / Steam Deck / Lenovo Legion Go / etc.)
  // is almost certainly a Windows handheld. Treat it as rog-ally for
  // detection purposes (controller-first UI). Desktop Windows laptops
  // with touch typically have larger displays.
  const isWindows = /windows nt/.test(ua);
  const hasTouch = (navigator.maxTouchPoints ?? 0) > 0 || 'ontouchstart' in (typeof window !== 'undefined' ? window : {});
  const w = typeof window !== 'undefined' ? window.innerWidth : 0;
  const h = typeof window !== 'undefined' ? window.innerHeight : 0;
  // Use SCREEN dimensions as a fallback when window is constrained
  // (windowed PWA mode). Handheld screens are 7-8" 1080p typically =
  // 1920x1200 or 1280x720 native.
  const sw = typeof screen !== 'undefined' ? (screen.width ?? 0) : 0;
  const sh = typeof screen !== 'undefined' ? (screen.height ?? 0) : 0;
  const longestEdge = Math.max(w, h, sw, sh);
  if (isWindows && hasTouch && longestEdge > 0 && longestEdge <= 1920 && Math.min(sw || w, sh || h) <= 1200) {
    return 'rog-ally';
  }
  return 'desktop';
}

/** True for any handheld gaming device (Steam Deck, ROG Ally, Switch).
 *  Used to show install hints + tweak default settings (zoom, controls). */
export function isHandheldGamingDevice(): boolean {
  const c = classifyDevice();
  return c === 'steam-deck' || c === 'rog-ally' || c === 'nintendo-switch';
}

/** Returns the `effectiveType` from navigator.connection if available,
 *  else 'unknown'. Used in mobile telemetry to spot slow-network bug
 *  patterns. Chrome/Edge/Android only — Safari returns 'unknown'. */
export function getNetworkClass(): string {
  if (typeof navigator === 'undefined') return 'unknown';
  const conn = (navigator as unknown as { connection?: { effectiveType?: string; saveData?: boolean } }).connection;
  if (!conn) return 'unknown';
  if (conn.saveData) return `${conn.effectiveType ?? 'unknown'}+saveData`;
  return conn.effectiveType ?? 'unknown';
}
